/**
 * DatePanel — datopanelet (#152, mangler i MAPPING.md §3).
 *
 * Toppen på Oppgaver og Plan, og «I dag»-flisen på Hjem (prinsipp 6 i
 * design/HANDOFF.md): mint → salvie → strå-gradienten (`bg-panel`, egen verdi
 * i hvert tema) med runde hjørner nederst (`rounded-b-2xl`, 32 px) og alltid
 * mørk tekst (`text-on-panel`).
 *
 * Innholdet er tittel (h1, `text-heading-l`, 26 px / 800), valgfri undertekst, en valgfri
 * handling til høyre for tittelen (f.eks. «Foreslå») og `children` under —
 * typisk WeekStrip eller SegmentedTabs med `tone="panel"`.
 *
 * `rounded="all"` gir flisvarianten med runde hjørner rundt.
 */
import type { ReactNode } from 'react';

/**
 * Panelflaten uten form og innhold — delt med «I dag»-flisen på Hjem (#154),
 * som er en lenke med egen layout og derfor ikke kan være en DatePanel.
 */
export const PANEL_SURFACE = 'bg-panel text-on-panel';

/**
 * Runde 44 px-knapper på panelet (forrige/neste, ny hendelse) — delt av
 * Kalender og Middag (#157). Kallstedet legger på fyll eller hover.
 */
export const PANEL_ROUND = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full';

interface DatePanelProps {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  /** Tilgjengelig navn på seksjonen; standard er tittelen. */
  label?: string;
  rounded?: 'bottom' | 'all';
  className?: string;
  'data-testid'?: string;
}

export function DatePanel({
  title,
  subtitle,
  action,
  children,
  label,
  rounded = 'bottom',
  className,
  'data-testid': testId = 'date-panel',
}: DatePanelProps): ReactNode {
  return (
    <section
      aria-label={label ?? title}
      data-testid={testId}
      className={[
        `${PANEL_SURFACE} px-5 pt-6 pb-4`,
        rounded === 'all' ? 'rounded-2xl' : 'rounded-b-2xl',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-center justify-between gap-3">
        <h1 className="m-0 text-heading-l font-extrabold tracking-tight">{title}</h1>
        {action}
      </div>
      {subtitle ? <p className="mt-1 mb-3 text-control font-bold">{subtitle}</p> : null}
      {children}
    </section>
  );
}
